import React, { useState, useEffect, useCallback } from 'react';
import { host } from '../script/variables';
import '../style/TableManager.css';

/* ── helpers ─────────────────────────────────────────────── */
const token = () => localStorage.getItem('partnertoken');
const api   = async (endpoint, body) => {
  const res  = await fetch(`${host}${endpoint}`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token() },
    body:    JSON.stringify(body)
  });
  return res.json();
};

const STATUS_META = {
  available: { label: 'Available', color: '#10b981', bg: '#d1fae5' },
  occupied:  { label: 'Occupied',  color: '#ef4444', bg: '#fee2e2' },
  reserved:  { label: 'Reserved',  color: '#f59e0b', bg: '#fef3c7' },
  blocked:   { label: 'Blocked',   color: '#6b7280', bg: '#f3f4f6' }
};

/** The status values the Table schema accepts — order drives the picker. */
const STATUS_ORDER = ['available', 'occupied', 'reserved', 'blocked'];

/* ── Toast ───────────────────────────────────────────────── */
const Toast = ({ toast }) => toast
  ? <div className={`tm-toast tm-toast-${toast.type}`}>{toast.msg}</div>
  : null;

/* ── AreaFormModal ───────────────────────────────────────── */
const AreaFormModal = ({ editing, onClose, onSave }) => {
  const [name,        setName]        = useState(editing?.name        || '');
  const [description, setDescription] = useState(editing?.description || '');
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');

  const handleSave = async () => {
    if (!name.trim()) { setError('Area name is required'); return; }
    setSaving(true);
    await onSave({ name, description });
    setSaving(false);
  };

  return (
    <div className="tm-backdrop">
      <div className="tm-modal">
        <div className="tm-modal-header">
          <h3>{editing ? 'Edit Area' : 'New Area'}</h3>
          <button className="tm-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="tm-field">
          <label>Area Name *</label>
          <input
            placeholder="e.g. Family Area, AC Room, Rooftop…"
            value={name}
            onChange={e => { setName(e.target.value); setError(''); }}
          />
          {error && <span className="tm-field-error">{error}</span>}
        </div>

        <div className="tm-field">
          <label>Description</label>
          <textarea
            placeholder="Optional notes about this area"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={2}
          />
        </div>

        <div className="tm-modal-actions">
          <button className="tm-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '⏳ Saving…' : '✓ Save Area'}
          </button>
          <button className="tm-btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

/* ── TableFormModal ──────────────────────────────────────── */
const TableFormModal = ({ editing, areas, onClose, onSave }) => {
  const [tableNumber, setTableNumber] = useState(editing?.tableNumber || '');
  const [capacity,    setCapacity]    = useState(editing?.capacity    || 2);
  const [areaId,      setAreaId]      = useState(editing?.areaId?._id || editing?.areaId || '');
  const [notes,       setNotes]       = useState(editing?.notes       || '');
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');

  const handleSave = async () => {
    if (!tableNumber.trim())    { setError('Table number is required'); return; }
    if (capacity < 1)           { setError('Capacity must be at least 1'); return; }
    setSaving(true);
    await onSave({ tableNumber, capacity: Number(capacity), areaId: areaId || null, notes });
    setSaving(false);
  };

  return (
    <div className="tm-backdrop">
      <div className="tm-modal">
        <div className="tm-modal-header">
          <h3>{editing ? 'Edit Table' : 'New Table'}</h3>
          <button className="tm-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="tm-field-row">
          <div className="tm-field">
            <label>Table Number *</label>
            <input
              placeholder="e.g. T1, A-3, VIP-1"
              value={tableNumber}
              onChange={e => { setTableNumber(e.target.value); setError(''); }}
            />
          </div>
          <div className="tm-field">
            <label>Capacity (seats) *</label>
            <input
              type="number" min="1" max="50"
              value={capacity}
              onChange={e => setCapacity(e.target.value)}
            />
          </div>
        </div>

        {error && <span className="tm-field-error">{error}</span>}

        <div className="tm-field">
          <label>Area</label>
          <select value={areaId} onChange={e => setAreaId(e.target.value)}>
            <option value="">— Unassigned (General Floor) —</option>
            {areas.map(a => (
              <option key={a._id} value={a._id}>{a.name}</option>
            ))}
          </select>
        </div>

        <div className="tm-field">
          <label>Notes</label>
          <input
            placeholder="e.g. Near window, corner table…"
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </div>

        <div className="tm-modal-actions">
          <button className="tm-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '⏳ Saving…' : '✓ Save Table'}
          </button>
          <button className="tm-btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

/* ── TableCard ───────────────────────────────────────────── */
const TableCard = ({ table, onEdit, onDelete, onStatusChange }) => {
  const meta    = STATUS_META[table.status] || STATUS_META.available;
  const [busy, setBusy] = useState(false);

  const handleStatusPick = async (next) => {
    if (next === table.status) return;
    setBusy(true);
    await onStatusChange(table._id, next);
    setBusy(false);
  };

  return (
    <div className="tm-table-card" style={{ borderTopColor: meta.color }}>
      <div className="tm-table-card-top">
        <span className="tm-table-number">{table.tableNumber}</span>

        {/* Status control. A native <select> on purpose: the card grid sits
            inside an overflow:hidden area section, so a custom dropdown would
            be clipped on the bottom row — and this gives phones the native
            picker for free. */}
        <span className={`tm-status-pill-wrap ${busy ? 'is-busy' : ''}`}>
          <select
            className="tm-status-pill tm-status-select"
            style={{ background: meta.bg, color: meta.color }}
            value={table.status}
            disabled={busy}
            onChange={e => handleStatusPick(e.target.value)}
            title={`Table ${table.tableNumber} is ${meta.label.toLowerCase()} — change status`}
            aria-label={`Status for table ${table.tableNumber}`}
          >
            {STATUS_ORDER.map(s => (
              <option key={s} value={s}>{STATUS_META[s].label}</option>
            ))}
          </select>
          <span className="tm-status-caret" style={{ color: meta.color }} aria-hidden="true">
            {busy ? '⏳' : '▾'}
          </span>
        </span>
      </div>

      <div className="tm-table-capacity">
        <span className="tm-cap-icon">👥</span>
        <span>{table.capacity} seats</span>
      </div>

      {table.notes && (
        <p className="tm-table-notes">{table.notes}</p>
      )}

      <div className="tm-table-card-actions">
        <button
          className="tm-card-btn tm-edit-btn"
          onClick={() => onEdit(table)}
          title="Edit table"
        >
          ✎
        </button>
        <button
          className="tm-card-btn tm-delete-btn"
          onClick={() => onDelete(table._id)}
          title="Delete table"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

/* ── AreaSection ─────────────────────────────────────────── */
const AreaSection = ({ area, tables, areas, onEditArea, onDeleteArea, onAddTable, onEditTable, onDeleteTable, onStatusChange }) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="tm-area-section">
      <div className="tm-area-header">
        <div className="tm-area-header-left">
          <button className="tm-collapse-btn" onClick={() => setCollapsed(c => !c)}>
            {collapsed ? '▸' : '▾'}
          </button>
          <div>
            <h3 className="tm-area-name">{area.name}</h3>
            {area.description && <p className="tm-area-desc">{area.description}</p>}
          </div>
          <div className="tm-area-stats">
            <span className="tm-stat-chip tm-stat-total">{tables.length} tables</span>
            <span className="tm-stat-chip tm-stat-avail">
              {tables.filter(t => t.status === 'available').length} free
            </span>
          </div>
        </div>

        <div className="tm-area-header-actions">
          <button className="tm-btn-add-table" onClick={() => onAddTable(area._id)}>
            + Table
          </button>
          <button className="tm-icon-btn" onClick={() => onEditArea(area)} title="Edit area">✎</button>
          <button className="tm-icon-btn tm-danger-icon" onClick={() => onDeleteArea(area._id)} title="Delete area">✕</button>
        </div>
      </div>

      {!collapsed && (
        <div className="tm-tables-grid">
          {tables.length === 0 ? (
            <p className="tm-area-empty">No tables yet — click "+ Table" to add one</p>
          ) : (
            tables.map(table => (
              <TableCard
                key={table._id}
                table={table}
                areas={areas}
                onEdit={onEditTable}
                onDelete={onDeleteTable}
                onStatusChange={onStatusChange}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
const TableManager = ({ cafeid }) => {
  const [areas,   setAreas]   = useState([]);
  const [tables,  setTables]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast,   setToast]   = useState(null);

  // modals
  const [areaModal,  setAreaModal]  = useState(null);  // null | 'new' | area-object
  const [tableModal, setTableModal] = useState(null);  // null | { editing?, defaultAreaId? }

  /* ── toast ── */
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  /* ── fetch ── */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ar, tb] = await Promise.all([
        api('/tables/areas',  { restaurantId: cafeid }),
        api('/tables/get',    { restaurantId: cafeid })
      ]);
      if (ar.success) setAreas(ar.data);
      if (tb.success) setTables(tb.data);
    } catch (err) {
      console.error('TableManager fetch error:', err);
      showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, [cafeid]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ── area actions ── */
  const handleSaveArea = async ({ name, description }) => {
    const isEdit = areaModal && areaModal !== 'new';
    const endpoint = isEdit ? '/tables/areas/update' : '/tables/areas/create';
    const body     = isEdit
      ? { areaId: areaModal._id, name, description }
      : { restaurantId: cafeid, name, description };

    const res = await api(endpoint, body);
    if (res.success) {
      showToast(isEdit ? 'Area updated' : 'Area created');
      setAreaModal(null);
      fetchAll();
    } else {
      showToast(res.message || 'Failed', 'error');
    }
  };

  const handleDeleteArea = async (areaId) => {
    if (!window.confirm('Delete this area? Tables will be unassigned.')) return;
    const res = await api('/tables/areas/delete', { areaId });
    if (res.success) { showToast('Area deleted'); fetchAll(); }
    else showToast(res.message || 'Failed', 'error');
  };

  /* ── table actions ── */
  const handleSaveTable = async ({ tableNumber, capacity, areaId, notes }) => {
    const isEdit   = tableModal?.editing;
    const endpoint = isEdit ? '/tables/update' : '/tables/create';
    const body     = isEdit
      ? { tableId: tableModal.editing._id, tableNumber, capacity, areaId, notes }
      : { restaurantId: cafeid, tableNumber, capacity, areaId: areaId || tableModal?.defaultAreaId || null, notes };

    const res = await api(endpoint, body);
    if (res.success) {
      showToast(isEdit ? 'Table updated' : 'Table added');
      setTableModal(null);
      fetchAll();
    } else {
      showToast(res.message || 'Failed', 'error');
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm('Delete this table?')) return;
    const res = await api('/tables/delete', { tableId });
    if (res.success) { showToast('Table deleted'); fetchAll(); }
    else showToast(res.message || 'Failed', 'error');
  };

  /* Goes through /tables/update — the same endpoint the edit modal uses. It
     accepts `status` and runs schema validators, so a bad value is rejected
     server-side rather than silently stored. */
  const handleStatusChange = async (tableId, status) => {
    try {
      const res = await api('/tables/update', { tableId, status });
      if (res.success) {
        setTables(prev => prev.map(t => t._id === tableId ? { ...t, status } : t));
        showToast(`Marked ${STATUS_META[status].label.toLowerCase()}`);
      } else {
        showToast(res.message || 'Failed to update status', 'error');
      }
    } catch (err) {
      console.error('TableManager status update error:', err);
      showToast('Failed to update status', 'error');
    }
  };

  /* ── group tables by area ── */
  const tablesByArea = (areaId) =>
    tables.filter(t => {
      const tid = t.areaId?._id?.toString() || t.areaId?.toString() || null;
      return tid === (areaId?.toString() || null);
    });

  const unassignedTables = tables.filter(t => !t.areaId || (typeof t.areaId === 'object' && !t.areaId._id));

  /* ── summary stats ── */
  const totalTables    = tables.length;
  const availableTables = tables.filter(t => t.status === 'available').length;
  const occupiedTables  = tables.filter(t => t.status === 'occupied').length;
  const totalSeats      = tables.reduce((s, t) => s + (t.capacity || 0), 0);

  if (loading) return (
    <div className="tm-wrap">
      <div className="tm-loading"><div className="tm-spinner" /><span>Loading floor plan…</span></div>
    </div>
  );

  return (
    <div className="tm-wrap">

      {/* ── Page header ── */}
      <div className="tm-page-header">
        <div className="tm-page-header-left">
          <h1>🪑 Table Management</h1>
          <p>Manage areas and tables for your restaurant floor</p>
        </div>
        <div className="tm-page-header-actions">
          <button className="tm-btn-secondary" onClick={() => setAreaModal('new')}>
            + New Area
          </button>
          <button className="tm-btn-primary" onClick={() => setTableModal({})}>
            + New Table
          </button>
        </div>
      </div>

      {/* ── Summary strip ── */}
      <div className="tm-summary-strip">
        <div className="tm-summary-card">
          <span className="tm-summary-val">{areas.length}</span>
          <span className="tm-summary-label">Areas</span>
        </div>
        <div className="tm-summary-card">
          <span className="tm-summary-val">{totalTables}</span>
          <span className="tm-summary-label">Tables</span>
        </div>
        <div className="tm-summary-card tm-summary-green">
          <span className="tm-summary-val">{availableTables}</span>
          <span className="tm-summary-label">Available</span>
        </div>
        <div className="tm-summary-card tm-summary-red">
          <span className="tm-summary-val">{occupiedTables}</span>
          <span className="tm-summary-label">Occupied</span>
        </div>
        <div className="tm-summary-card">
          <span className="tm-summary-val">{totalSeats}</span>
          <span className="tm-summary-label">Total Seats</span>
        </div>
      </div>

      {/* ── Status legend ── */}
      <div className="tm-legend">
        {Object.entries(STATUS_META).map(([key, { label, color, bg }]) => (
          <span key={key} className="tm-legend-item" style={{ background: bg, color }}>
            ● {label}
          </span>
        ))}
        <span className="tm-legend-hint">Tap the status pill on a card to change it</span>
      </div>

      {/* ── Empty state ── */}
      {areas.length === 0 && tables.length === 0 && (
        <div className="tm-empty-state">
          <div className="tm-empty-icon">🪑</div>
          <h3>No tables yet</h3>
          <p>Start by creating an area (e.g. "AC Room", "Family Section") then add tables to it.</p>
          <button className="tm-btn-primary" onClick={() => setAreaModal('new')}>
            + Create First Area
          </button>
        </div>
      )}

      {/* ── Areas with their tables ── */}
      {areas.map(area => (
        <AreaSection
          key={area._id}
          area={area}
          tables={tablesByArea(area._id)}
          areas={areas}
          onEditArea={a => setAreaModal(a)}
          onDeleteArea={handleDeleteArea}
          onAddTable={areaId => setTableModal({ defaultAreaId: areaId })}
          onEditTable={t => setTableModal({ editing: t })}
          onDeleteTable={handleDeleteTable}
          onStatusChange={handleStatusChange}
        />
      ))}

      {/* ── Unassigned tables ── */}
      {unassignedTables.length > 0 && (
        <div className="tm-area-section tm-unassigned">
          <div className="tm-area-header">
            <div className="tm-area-header-left">
              <h3 className="tm-area-name">📋 Unassigned Tables</h3>
              <span className="tm-stat-chip tm-stat-total">{unassignedTables.length} tables</span>
            </div>
          </div>
          <div className="tm-tables-grid">
            {unassignedTables.map(table => (
              <TableCard
                key={table._id}
                table={table}
                areas={areas}
                onEdit={t => setTableModal({ editing: t })}
                onDelete={handleDeleteTable}
                onStatusChange={handleStatusChange}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {areaModal && (
        <AreaFormModal
          editing={areaModal === 'new' ? null : areaModal}
          onClose={() => setAreaModal(null)}
          onSave={handleSaveArea}
        />
      )}

      {tableModal !== null && (
        <TableFormModal
          editing={tableModal?.editing || null}
          areas={areas}
          onClose={() => setTableModal(null)}
          onSave={handleSaveTable}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
};

export default TableManager;